"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type DistanceCtx,
  EndpointResults,
  MeasureResults,
} from "@/components/tools/linear-algebra/distance-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  isSamePoint,
  type Point,
} from "@/lib/tool-kit/linear-algebra/analytic";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  type NumberStyle,
  parseRational,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const DISTANCE_AND_MIDPOINT_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to find?",
  modes: {
    measure: "Distance, midpoint and section point",
    endpoint: "The other endpoint, from the midpoint",
  },
  dimensionLabel: "Dimension",
  dimensions: { "2": "2D (x, y)", "3": "3D (x, y, z)" },
  pointOne: "Point P₁",
  pointTwo: "Point P₂",
  pointMid: "Midpoint M",
  coordinate: "{name}, {axis}",
  ratioLegend: "Section formula: divide P₁P₂ in the ratio m : n",
  ratioM: "m",
  ratioN: "n",
  ratioKindLabel: "Division",
  ratioKinds: {
    internal: "Internal (a point on the segment)",
    external: "External (a point on the line beyond an end)",
  },
  entryHint: "Whole numbers, decimals or fractions such as 3/4.",
  results: "Results",
  enterAll: "Fill in every coordinate to see the results.",
  invalid:
    "Enter each value as a number or a fraction, such as 2, −0.5 or 3/4.",
  samePoint:
    "The two points are the same, so the distance is 0 and the midpoint is the point itself.",
  copied: "Copied!",
  copyResults: "Copy results",
  distanceLabel: "Distance",
  midpointLabel: "Midpoint",
  squaredLabel: "Distance squared",
  endpointLabel: "Other endpoint P₂",
  workingHeading: "The working",
  distanceFormula: "d = √(sum of the squared differences in {axes})",
  distanceWorking:
    "d = √({terms}) = √({squares}) = √({parts}) = √{sum} = {result}",
  noteSimplified: "√{n} = √({square} × {rad}) = {coef}√{rad}",
  noteSimplest:
    "{n} has no square factor, so √{n} is already in simplest radical form.",
  midpointFormula: "M = ((x₁ + x₂) / 2, (y₁ + y₂) / 2, …)",
  midpointWorking: "M = ({working}) = {result}",
  endpointFormula: "P₂ = 2M − P₁, because M is halfway between P₁ and P₂",
  sectionHeading: "Section formula",
  sectionInternalLabel: "Point dividing P₁P₂ internally {m} : {n}",
  sectionExternalLabel: "Point dividing P₁P₂ externally {m} : {n}",
  sectionInternalFormula: "S = (n·P₁ + m·P₂) / (m + n)",
  sectionExternalFormula: "S = (m·P₂ − n·P₁) / (m − n)",
  sectionInternal: "S = ({n} × P₁ + {m} × P₂) / ({m} + {n}) = {result}",
  sectionExternal: "S = ({m} × P₂ − {n} × P₁) / ({m} − {n}) = {result}",
  externalSide: {
    q: "Because m is larger than n, S lies beyond P₂.",
    p: "Because m is smaller than n, S lies beyond P₁.",
  },
  badRatio: "Enter m and n as positive numbers.",
  equalExternal:
    "An external division in the ratio 1 : 1 does not exist: the line through the two points never reaches such a point.",
  plotHeading: "Diagram",
  plotTitle:
    "The segment from P₁ to P₂ drawn to scale, with the midpoint M and any other point marked",
  pointText: "({x}, {y})",
  noPlot3d:
    "Three-dimensional points are not drawn; the exact results above are complete.",
  downloadPng: "Download diagram (PNG)",
} as const;

export type DistanceStrings = typeof DISTANCE_AND_MIDPOINT_CALCULATOR_STRINGS;

type Mode = keyof DistanceStrings["modes"];
type Dimension = keyof DistanceStrings["dimensions"];
type Kind = keyof DistanceStrings["ratioKinds"];

const AXES = ["x", "y", "z"] as const;
const DECIMAL_INPUT = /[.,]/;
const MODE_ORDER: Mode[] = ["measure", "endpoint"];

function parsePoint(texts: string[]): Point | "empty" | "invalid" {
  if (texts.some((text) => text.trim() === "")) {
    return "empty";
  }
  const parsed = texts.map(parseRational);
  return parsed.some((x) => x === null) ? "invalid" : (parsed as Rational[]);
}

function PointInputs({
  legend,
  name,
  idPrefix,
  dimension,
  values,
  onChange,
  strings,
}: {
  legend: string;
  name: string;
  idPrefix: string;
  dimension: number;
  values: string[];
  onChange: (index: number, value: string) => void;
  strings: DistanceStrings;
}) {
  return (
    <fieldset className="space-y-3 rounded-lg border border-border p-4">
      <legend className="px-1 font-medium text-foreground text-sm">
        {legend}
      </legend>
      <div className="grid grid-cols-3 gap-3">
        {AXES.slice(0, dimension).map((axis, i) => (
          <TextField
            id={`${idPrefix}-${axis}`}
            key={axis}
            label={t(strings.coordinate, { name, axis })}
            mono
            numeric
            onChange={(value) => onChange(i, value)}
            value={values[i]}
          />
        ))}
      </div>
    </fieldset>
  );
}

function Outcome({
  mode,
  n,
  p1,
  p2,
  mid,
  ratio,
  ctx,
}: {
  mode: Mode;
  n: number;
  p1: string[];
  p2: string[];
  mid: string[];
  ratio: { m: string; n: string; kind: Kind };
  ctx: DistanceCtx;
}) {
  const { strings } = ctx;
  const first = parsePoint(p1.slice(0, n));
  const second = parsePoint((mode === "measure" ? p2 : mid).slice(0, n));
  if (first === "empty" || second === "empty") {
    return <Notice>{strings.enterAll}</Notice>;
  }
  if (first === "invalid" || second === "invalid") {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  if (mode === "endpoint") {
    return <EndpointResults ctx={ctx} m={second} p={first} />;
  }
  if (isSamePoint(first, second)) {
    return <Notice>{strings.samePoint}</Notice>;
  }
  const m = parseRational(ratio.m);
  const nn = parseRational(ratio.n);
  const parsedRatio = m && nn ? { m, n: nn, kind: ratio.kind } : null;
  return <MeasureResults ctx={ctx} p={first} q={second} ratio={parsedRatio} />;
}

export default function DistanceAndMidpointCalculatorWidget() {
  const strings = useToolUi(DISTANCE_AND_MIDPOINT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("measure");
  const [dimension, setDimension] = useState<Dimension>("2");
  const [p1, setP1] = useState(["-2", "3", "0"]);
  const [p2, setP2] = useState(["4", "-1", "2"]);
  const [mid, setMid] = useState(["1", "1", "1"]);
  const [ratioM, setRatioM] = useState("1");
  const [ratioN, setRatioN] = useState("2");
  const [kind, setKind] = useState<Kind>("internal");
  const n = Number(dimension);
  const update = (set: typeof setP1) => (index: number, value: string) =>
    set((prev) => prev.map((x, i) => (i === index ? value : x)));
  const shown = [
    ...p1.slice(0, n),
    ...(mode === "measure"
      ? [...p2.slice(0, n), ratioM, ratioN]
      : mid.slice(0, n)),
  ];
  const style: NumberStyle = shown.some((text) => DECIMAL_INPUT.test(text))
    ? "decimal"
    : "fraction";
  const ctx: DistanceCtx = { strings, style, mark: decimalMarkOf(fmt), fmt };
  return (
    <>
      <div className="flex flex-wrap gap-6">
        <PillGroup
          label={strings.modeLabel}
          name="dm-mode"
          onChange={setMode}
          options={MODE_ORDER.map((id) => ({
            value: id,
            label: strings.modes[id],
          }))}
          value={mode}
        />
        <PillGroup
          label={strings.dimensionLabel}
          name="dm-dim"
          onChange={setDimension}
          options={(["2", "3"] as const).map((id) => ({
            value: id,
            label: strings.dimensions[id],
          }))}
          value={dimension}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <PointInputs
          dimension={n}
          idPrefix="dm-p1"
          legend={strings.pointOne}
          name="P₁"
          onChange={update(setP1)}
          strings={strings}
          values={p1}
        />
        {mode === "measure" ? (
          <PointInputs
            dimension={n}
            idPrefix="dm-p2"
            legend={strings.pointTwo}
            name="P₂"
            onChange={update(setP2)}
            strings={strings}
            values={p2}
          />
        ) : (
          <PointInputs
            dimension={n}
            idPrefix="dm-mid"
            legend={strings.pointMid}
            name="M"
            onChange={update(setMid)}
            strings={strings}
            values={mid}
          />
        )}
      </div>
      {mode === "measure" ? (
        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="px-1 font-medium text-foreground text-sm">
            {strings.ratioLegend}
          </legend>
          <div className="grid gap-3 sm:grid-cols-[6rem_6rem_1fr]">
            <TextField
              id="dm-m"
              label={strings.ratioM}
              mono
              numeric
              onChange={setRatioM}
              value={ratioM}
            />
            <TextField
              id="dm-n"
              label={strings.ratioN}
              mono
              numeric
              onChange={setRatioN}
              value={ratioN}
            />
            <PillGroup
              label={strings.ratioKindLabel}
              name="dm-kind"
              onChange={setKind}
              options={(["internal", "external"] as const).map((id) => ({
                value: id,
                label: strings.ratioKinds[id],
              }))}
              value={kind}
            />
          </div>
        </fieldset>
      ) : null}
      <p className="text-muted-foreground text-xs">{strings.entryHint}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          ctx={ctx}
          mid={mid}
          mode={mode}
          n={n}
          p1={p1}
          p2={p2}
          ratio={{ m: ratioM, n: ratioN, kind }}
        />
      </section>
    </>
  );
}
